import { useState, useEffect, useCallback } from "react";
import { HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileUploadInput } from "@/components/ui/file-upload-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import {
  useFileUploader,
  DOCUMENT_TYPES,
  Document
} from "@/components/features/FileUploader";

interface RecognitionStatusFieldsProps {
  profile: any;
  setProfile: (profile: any) => void;
  readOnly: boolean;
  loading: boolean;
}

export function RecognitionStatusFields({
  profile,
  setProfile,
  readOnly,
  loading
}: RecognitionStatusFieldsProps) {
  const {
    uploadTalentProfileDocument,
    getTalentDocument,
    deleteFile,
    handleViewFile
  } = useFileUploader();

  const [deficitNoticeDocument, setDeficitNoticeDocument] = useState<
    Document | undefined
  >(undefined);
  const [uploading, setUploading] = useState(false);
  const [loadingDoc, setLoadingDoc] = useState(false);

  const fetchDocument = useCallback(async () => {
    setLoadingDoc(true);
    const res = await getTalentDocument(
      profile.id,
      DOCUMENT_TYPES.DEFICIT_NOTICE_DOCUMENT
    );
    if (res.success) setDeficitNoticeDocument(res.data[0]);
    setLoadingDoc(false);
  }, [profile.id, getTalentDocument]);

  useEffect(() => {
    if (profile?.id) fetchDocument();
  }, [profile?.id, fetchDocument]);

  const handleFileUpload = async (file: File) => {
    setUploading(true);
    const result = await uploadTalentProfileDocument(
      file,
      DOCUMENT_TYPES.DEFICIT_NOTICE_DOCUMENT,
      profile.id,
      {
        values: { talent_id: profile.id }
      }
    );
    if (result.success) await fetchDocument();
    setUploading(false);
  };

  const isEducatedInGermany =
    profile?.education_country &&
    (profile.education_country.toLowerCase().includes("germany") ||
      profile.education_country.toLowerCase().includes("deutschland") ||
      profile.education_country.toLowerCase() === "de");

  return (
    <>
      <div className="space-y-1 col-span-2">
        <div className="flex items-center gap-1">
          <label className="text-sm font-medium">
            Berufsanerkennung Status
          </label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <HelpCircle className="h-4 w-4 text-gray-400" />
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {isEducatedInGermany
                    ? "Bei einer Ausbildung in Deutschland ist die Berufsanerkennung automatisch anerkannt."
                    : "Wie ist der Status deiner Berufsanerkennung?"}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <Select
          value={
            isEducatedInGermany
              ? "approved"
              : profile?.recognition_status || "not_applied"
          }
          onValueChange={(value) =>
            setProfile({ ...profile!, recognition_status: value })
          }
          disabled={isEducatedInGermany || readOnly}
        >
          <SelectTrigger loading={loading}>
            <SelectValue placeholder="Bitte auswählen" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="not_applied">Nicht beantragt</SelectItem>
            <SelectItem value="pending">Beantragt - Ausstehend</SelectItem>
            <SelectItem value="partially_approved">
              Teilweise anerkannt (Defizitbescheid)
            </SelectItem>
            <SelectItem value="approved">Anerkannt</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {profile?.recognition_status === "partially_approved" && (
        <>
          <div className="space-y-1 col-span-2">
            <label className="text-sm font-medium">
              Defizitbescheid hochladen
            </label>
            <div className="flex items-center gap-2">
              <FileUploadInput
                currentFileName={deficitNoticeDocument?.file_name}
                accept=".pdf,.doc,.docx"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileUpload(file);
                }}
                disabled={readOnly}
                loading={loading || loadingDoc || uploading}
                onClearFile={async () => {
                  if (deficitNoticeDocument) {
                    const success = await deleteFile(
                      "profiles",
                      deficitNoticeDocument.id,
                      deficitNoticeDocument.file_path
                    );
                    if (success) fetchDocument();
                  }
                }}
              />
              {deficitNoticeDocument && (
                <Button
                  disabled={readOnly}
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    handleViewFile("profiles", deficitNoticeDocument.file_path)
                  }
                >
                  Anzeigen
                </Button>
              )}
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">
              Fehlende Theoriestunden
            </label>
            <Input
              disabled={readOnly}
              loading={loading}
              type="number"
              value={profile.theory_hours_deficit || ""}
              onChange={(e) =>
                setProfile({
                  ...profile,
                  theory_hours_deficit: e.target.value
                    ? parseInt(e.target.value)
                    : null
                })
              }
              placeholder="z.B. 450"
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">
              Fehlende Praxisstunden
            </label>
            <Input
              disabled={readOnly}
              loading={loading}
              type="number"
              value={profile.practice_hours_deficit || ""}
              onChange={(e) =>
                setProfile({
                  ...profile,
                  practice_hours_deficit: e.target.value
                    ? parseInt(e.target.value)
                    : null
                })
              }
              placeholder="z.B. 750"
            />
          </div>
        </>
      )}
    </>
  );
}
